import {
  Button,
  DialogTitle,
  Stack,
  Typography,
  TextField,
  Card
} from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";

import { useBarcodeScanner } from "~/lib/barCodeScan";
import { getBarcodeValue } from "~/lib/helpers";
import { mixpanelTrack, useMixpanelModalName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";

import { usePostPutawayBinNotEmptyMutation } from "~/redux/warehouse/inventory.hooks";

type BinNotEmptyModalProps = {
  binId: Guid | null | undefined;
  portId?: number | undefined;
  setIsBinNotEmptyPanelOpen: Dispatch<SetStateAction<boolean>>;
  handleSetBinNotEmpty?: (productIdentifier: string) => void;
  onSuccess?: () => Promise<void> | void;
};

export function BinNotEmptyModal(props: BinNotEmptyModalProps) {
  const { portId, binId, onSuccess } = props;
  const { errorToast, successToast } = useToast();
  const { t } = useTranslation();
  const [scannedBarcode, setScannedBarcode] = useState<string>("");
  const [productInBin, setProductInBin] = useState<boolean>(false);
  const trackedModalName = useMixpanelModalName(
    "Please scan the product in the bin"
  );

  const [setBinNotEmpty, { isLoading, error, isSuccess, isUninitialized }] =
    usePostPutawayBinNotEmptyMutation();

  useEffect(() => {
    if (isSuccess) {
      successToast(`Hold placed on UPC ${scannedBarcode}`);
    } else if (error) {
      errorToast(getMessageFromRtkError(error), {
        trackingLocation: trackedModalName
      });
    }
  }, [
    error,
    isSuccess,
    errorToast,
    scannedBarcode,
    successToast,
    trackedModalName
  ]);

  const newSelectedCompartment = useAppSelector(
    (state) => state.autostorePutaway.newSelectedCompartment
  );

  const siteWorkstation = useAppSelector(
    (state) => state.workstations.siteWorkstation
  );

  const submitManualEnteredBarcode = (ev: KeyboardEvent) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      mixpanelTrack({
        trackedPageName: trackedModalName,
        type: "Input Change",
        label: "Manual UPC Entry",
        eventProperties: {
          Upc: scannedBarcode,
          BinId: binId
        }
      });
      setProductInBin(true);
    }
  };

  useBarcodeScanner({
    findScanMatch: (initialBuffer: string) => {
      const barcodeValue: string = getBarcodeValue(initialBuffer);
      setScannedBarcode(barcodeValue);
      setProductInBin(true);
      mixpanelTrack({
        trackedPageName: trackedModalName,
        type: "Scan",
        label: "Scan Product In Bin",
        eventProperties: {
          Upc: barcodeValue,
          BinId: binId
        }
      });
    }
  });

  const placeBinNotEmptyFetchNextBin = async (upc: string, exit: boolean) => {
    if (portId && binId && newSelectedCompartment != null && siteWorkstation) {
      const { autostoreGridId, id: workstationId } = siteWorkstation;
      try {
        await setBinNotEmpty({
          productIdentifier: upc,
          binId,
          portId,
          gridId: autostoreGridId,
          workstationId
        }).unwrap();
      } catch (e: unknown) {
        if ((e as FetchBaseQueryError).status === 404 && !exit) {
          // if initial attempt to set bin not empty fails, try the unprocessed scanned barcode value
          await placeBinNotEmptyFetchNextBin(scannedBarcode, true);
        }
      }
    }
  };

  const handleConfirm = async () => {
    if (productInBin) {
      const barcode = getBarcodeValue(scannedBarcode);
      mixpanelTrack({
        trackedPageName: trackedModalName,
        type: "Button Click",
        label: "Confirm",
        eventProperties: {
          Upc: barcode,
          BinId: binId
        }
      });
      if (props.handleSetBinNotEmpty) props.handleSetBinNotEmpty(barcode);
      else {
        await placeBinNotEmptyFetchNextBin(barcode, false);
      }

      props.setIsBinNotEmptyPanelOpen(false);
      if (onSuccess) await onSuccess();
    }
  };

  return (
    <Card
      role="dialog"
      aria-modal="true"
      aria-labelledby="bin-not-empty-modal-header"
    >
      <DialogTitle id="bin-not-empty-modal-header">
        {t("please scan the product in the bin")}
      </DialogTitle>
      <Stack flexDirection="column" gap={1} p={4}>
        <TextField
          id="scannedBarcode"
          type="string"
          label="UPC"
          fullWidth
          value={scannedBarcode}
          focused={!!scannedBarcode}
          inputProps={{
            sx: {
              fontSize: "40px",
              padding: "35px 10px 35px 18px",
              textAlign: "center"
            }
          }}
          onChange={(event) => setScannedBarcode(event.target.value)}
          onKeyDown={submitManualEnteredBarcode}
        />
        {productInBin && (
          <Stack gap={1} alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: "800" }}>
              {t("confirm when the product is in the bin")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={!isUninitialized || isLoading}
            >
              {t("confirm")}
            </Button>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
